import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import toast from "react-hot-toast";
import { axiosDelete, axiosGet, axiosPost, axiosPut } from "../../utils/common/axios";
import { extractCompanyId, extractUserId } from "../../utils/userRole";
import { patchEntityList } from "../actions";

export const extractPayloadAndCallback = (action) => {
  let payload = action?.data?.payload ?? action?.payload ?? action?.data ?? {};
  let callback = action?.data?.callback ?? action?.payload?.callback ?? action?.callback;
  if (payload && typeof payload === "object" && payload.payload && typeof payload.payload === "object") {
    callback = payload.callback || callback;
    payload = payload.payload;
  }
  if (payload && typeof payload === "object" && "callback" in payload) {
    const { callback: cb, ...rest } = payload;
    callback = cb || callback;
    payload = rest;
  }
  return { payload: payload ?? {}, callback };
};

export function* selectCompanyId(payload) {
  if (payload?.companyId) return payload.companyId;
  const auth = yield select((state) => state.auth);
  return extractCompanyId(auth);
}

export function* selectUserId(payload) {
  if (payload?.userId) return payload.userId;
  const auth = yield select((state) => state.auth);
  return extractUserId(auth?.loginUser) ?? extractUserId(auth);
}

export const toQuery = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    if (Array.isArray(value)) value.forEach((v) => search.append(key, v));
    else search.append(key, value);
  });
  const qs = search.toString();
  return qs ? `?${qs}` : "";
};

export const errorMessage = (e, fallback) =>
  e?.data?.message || (typeof e?.data === "string" && e.data) || e?.data?.error || e?.message || fallback;

export const notifySuccess = (message) => {
  toast.dismiss();
  toast.success(message);
};

export const notifyError = (e, fallback) => {
  if (e?.status === 401) return;
  toast.dismiss();
  toast.error(errorMessage(e, fallback));
};

const stripFields = (obj, fields = []) => {
  if (!obj || typeof obj !== "object") return obj;
  const next = { ...obj };
  fields.forEach((f) => delete next[f]);
  delete next.__optimistic;
  return next;
};

/**
 * Builds the standard list/get/create/update/delete sagas for one backend resource.
 *
 * scope:
 *   "company"  -> list requires ?companyId (taken from auth when not passed)
 *   "user"     -> list requires ?userId
 *   "none"     -> list is sent as-is
 * requiredParam: a list param without which the backend would return every tenant's rows,
 *   so the list is emptied instead of called.
 * computedFields: read-only fields the backend derives on GET; never sent back on POST/PUT.
 */
export function createEntitySagas(config) {
  const {
    entity,
    endpoint,
    label,
    scope = "company",
    requiredParam,
    injectCompanyId = false,
    computedFields = [],
    quiet = false,
    selectSlice,
    listKey,
    types: t,
    actions: a,
  } = config;

  const selectList = (state) => selectSlice(state)?.[listKey] || [];
  const selectQuery = (state) => selectSlice(state)?.query || {};

  function* buildListParams(payload) {
    const { silent, ...params } = payload || {};
    if (scope === "company") params.companyId = yield* selectCompanyId(params);
    if (scope === "user") params.userId = yield* selectUserId(params);
    return params;
  }

  function* refresh() {
    const query = yield select(selectQuery);
    yield put({ type: t.getAll, payload: { ...query, silent: true } });
  }

  function* getAll(action) {
    const { payload, callback } = extractPayloadAndCallback(action);
    try {
      const params = yield* buildListParams(payload);
      if ((scope === "company" && !params.companyId) || (scope === "user" && !params.userId) || (requiredParam && !params[requiredParam])) {
        yield put(a.getAllResponse([]));
        callback?.success?.([]);
        return;
      }
      const { data } = yield call(axiosGet, `${endpoint}${toQuery(params)}`);
      const list = Array.isArray(data) ? data : [];
      yield put(a.getAllResponse(list));
      callback?.success?.(list);
    } catch (e) {
      yield put(a.getAllResponse([]));
      if (!payload?.silent) notifyError(e, `Could not load ${label.toLowerCase()} list`);
      callback?.failure?.(e);
    }
  }

  function* getById(action) {
    const { payload, callback } = extractPayloadAndCallback(action);
    try {
      const id = payload?.id ?? payload;
      // Sent so the backend can enforce tenant isolation on GET /{id}; harmless for entities
      // whose controller doesn't (yet) look at it.
      const params = scope === "company" ? { companyId: yield* selectCompanyId(payload) } : {};
      const { data } = yield call(axiosGet, `${endpoint}/${id}${toQuery(params)}`);
      yield put(a.getByIdResponse(data));
      callback?.success?.(data);
    } catch (e) {
      yield put(a.getByIdResponse(null));
      notifyError(e, `Could not load ${label.toLowerCase()}`);
      callback?.failure?.(e);
    }
  }

  function* create(action) {
    const { payload, callback } = extractPayloadAndCallback(action);
    const tempId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    try {
      let body = stripFields(payload, [...computedFields, "id"]);
      if (injectCompanyId && !body.companyId) body = { ...body, companyId: yield* selectCompanyId(body) };
      yield put(patchEntityList(entity, "prepend", { item: { ...body, id: tempId, __optimistic: true } }));
      const { data } = yield call(axiosPost, endpoint, body);
      yield put(patchEntityList(entity, "replace", { id: tempId, item: { ...body, ...data } }));
      callback?.success?.(data);
      if (!quiet) notifySuccess(`${label} created successfully`);
      yield* refresh();
    } catch (e) {
      yield put(patchEntityList(entity, "remove", { id: tempId }));
      notifyError(e, `Could not create ${label.toLowerCase()}`);
      callback?.failure?.(e);
    }
  }

  // PUT is a full overwrite on this backend, so the stored record is merged with the changes.
  function* resolveFullRecord(id) {
    const list = yield select(selectList);
    const existing = list.find((row) => String(row.id) === String(id));
    if (existing) return existing;
    const { data } = yield call(axiosGet, `${endpoint}/${id}`);
    return data;
  }

  function* update(action) {
    const { payload, callback } = extractPayloadAndCallback(action);
    const id = payload?.id;
    let previous = null;
    try {
      previous = yield* resolveFullRecord(id);
      yield put(patchEntityList(entity, "merge", { id, changes: payload }));
      const body = stripFields({ ...previous, ...payload, id }, computedFields);
      const { data } = yield call(axiosPut, `${endpoint}/${id}`, body);
      callback?.success?.(data);
      if (!quiet) notifySuccess(`${label} updated successfully`);
      yield* refresh();
    } catch (e) {
      if (previous) yield put(patchEntityList(entity, "replace", { id, item: previous }));
      notifyError(e, `Could not update ${label.toLowerCase()}`);
      callback?.failure?.(e);
    }
  }

  function* remove(action) {
    const { payload, callback } = extractPayloadAndCallback(action);
    const id = payload?.id ?? payload;
    const list = yield select(selectList);
    const index = list.findIndex((row) => String(row.id) === String(id));
    const removed = index >= 0 ? list[index] : null;
    try {
      yield put(patchEntityList(entity, "remove", { id }));
      yield call(axiosDelete, `${endpoint}/${id}`);
      callback?.success?.(id);
      if (!quiet) notifySuccess(`${label} deleted successfully`);
    } catch (e) {
      if (removed) yield put(patchEntityList(entity, "insertAt", { index, item: removed }));
      notifyError(e, `Could not delete ${label.toLowerCase()}`);
      callback?.failure?.(e);
    }
  }

  function* watcher() {
    yield takeLatest(t.getAll, getAll);
    if (t.getById) yield takeLatest(t.getById, getById);
    if (t.create) yield takeEvery(t.create, create);
    if (t.update) yield takeEvery(t.update, update);
    if (t.remove) yield takeEvery(t.remove, remove);
  }

  return { watcher, refresh, resolveFullRecord, selectList };
}

/**
 * Runs a workflow action endpoint optimistically: applies `changes` to the row right away,
 * swaps in the server's record on success, and restores the original row on failure.
 */
export function* runOptimisticAction({ entity, selectList, id, changes, request, successMessage, errorFallback, callback, replaceWithResponse = true }) {
  const list = yield select(selectList);
  const previous = list.find((row) => String(row.id) === String(id));
  try {
    if (changes) yield put(patchEntityList(entity, "merge", { id, changes }));
    const { data } = yield call(request);
    if (replaceWithResponse && data && typeof data === "object" && data.id !== undefined) {
      yield put(patchEntityList(entity, "merge", { id, changes: data }));
    }
    callback?.success?.(data);
    if (successMessage) notifySuccess(successMessage);
    return data;
  } catch (e) {
    if (previous) yield put(patchEntityList(entity, "replace", { id, item: previous }));
    notifyError(e, errorFallback);
    callback?.failure?.(e);
    return undefined;
  }
}
